import { FC, useState, useEffect, PropsWithChildren } from 'react'
import Button, { TProps as TButtonProps } from 'components/Button'
import { getAppVersion } from 'lib/experiment'
import { IconShare, IconShareIos } from 'icons'
import Typography from 'components/Typography'
import classNames from 'classnames'
import styles from './ShareButton.module.scss'
import { postWebviewMessage } from 'lib/webview'

export type TProps = {
  share: {
    title: string
    text: string
    url: string
  }
  props?: TButtonProps
  onClick?: () => void
  onShareSuccess?: () => void
  onShareError?: (error: any) => void
  onClipboardSuccess?: () => void
  onClipboardError?: (error: any) => void
}

const ShareButton: FC<PropsWithChildren<TProps>> = ({
  children,
  share,
  props,
  onClick,
  onShareSuccess,
  onShareError,
  onClipboardSuccess,
  onClipboardError,
}) => {
  const [canShare, setCanShare] = useState<boolean>(false)
  const [canCopy, setCanCopy] = useState<boolean>(false)
  const [isAndroid, setAndroid] = useState<boolean>(false)
  const [copied, setCopy] = useState<boolean>(false)

  const isWebview = !!getAppVersion()

  useEffect(() => {
    setCanShare(
      !!(navigator?.canShare && navigator?.canShare(share)) || isWebview,
    )
    setCanCopy(!!navigator?.clipboard)
    setAndroid(navigator?.userAgent?.toLowerCase().includes('android'))
  }, [])

  if (!canShare && !canCopy) return null

  const shareIcon = isAndroid ? IconShare : IconShareIos
  const IconComponent = canShare ? shareIcon : IconShare

  const buttonProps = {
    ...props,
    'aria-label': canShare ? 'Compartilhar' : 'Copiar a URL atual',
    Icon: {
      Component: IconComponent,
      title: canShare ? 'Ícone: compartilhar' : 'Ícone: copiar',
      color: 'grey900',
    },
  } as TButtonProps

  const shareUrl = () => {
    if (isWebview) {
      postWebviewMessage('app-webview-share-listing', share)
    } else {
      navigator
        .share({
          title: share.title,
          text: share.text,
          url: share.url,
        })
        .then(() => {
          if (onShareSuccess) onShareSuccess()
        })
        .catch((error) => {
          if (onShareError) onShareError(error)
        })
    }
  }

  const copyToClipboard = () => {
    setCopy(false)
    navigator.clipboard
      .writeText(share.url)
      .then(() => {
        if (onClipboardSuccess) onClipboardSuccess()
        setCopy(true)
      })
      .catch((error) => {
        if (onClipboardError) onClipboardError(error)
      })
  }

  const handleClick = () => {
    if (onClick) {
      onClick()
    }
    if (canShare) {
      shareUrl()
    } else if (canCopy) {
      copyToClipboard()
    }
  }

  const labelClassNames = classNames(styles.ecShareButtonLabel, {
    [styles.ecShareButtonLabelCopied]: copied,
  })

  return (
    <div className={styles.ecShareButton}>
      <Button {...buttonProps} onClick={handleClick}>
        {children}
      </Button>
      <Typography variant="Label" className={labelClassNames}>
        URL copiada!
      </Typography>
    </div>
  )
}

export default ShareButton
